import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import NovelCard from '@/components/feature/NovelCard';
import { categories, getNovelsByCategory } from '@/mocks/novels';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug);
  const categoryNovels = slug ? getNovelsByCategory(slug) : [];
  const displayName = currentCategory?.name || '小说分类';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${displayName} - 可爱小说推荐`,
    description: currentCategory?.description || `精选${displayName}推荐书单`,
    url: `https://keaixiaoshuo.com/category/${slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: categoryNovels.map((novel, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'Book',
          name: novel.title,
          author: { '@type': 'Person', name: novel.author },
          url: `https://keaixiaoshuo.com/article/${novel.id}`,
        },
      })),
    },
  };

  return (
    <>
      <SEO
        title={`${displayName}推荐书单 - 可爱小说推荐 | 最萌治愈系小说合集`}
        description={currentCategory?.description || `精选${displayName}推荐，最全${displayName}书单合集，让你沉浸在暖心故事中。可爱小说推荐平台用心甄选每一本好小说。`}
        keywords={`${displayName},${displayName}推荐,可爱小说,萌系小说,甜宠小说,治愈系小说,好看的可爱小说`}
        canonicalUrl={`https://keaixiaoshuo.com/category/${slug}`}
      />
      <ScrollToTop />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <section className="pt-20 md:pt-24 pb-6 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <Breadcrumb
              items={[
                { name: '首页', path: '/' },
                { name: displayName },
              ]}
            />
          </div>
          <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 text-center">
            <h1 className="text-2xl md:text-4xl font-heading text-foreground-950 mb-3">
              {displayName}
            </h1>
            <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
              {currentCategory?.description}
            </p>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="w-full px-4 md:px-6 py-6 border-b border-background-200/60 bg-background-50 sticky top-16 z-40">
          <div className="max-w-6xl mx-auto overflow-x-auto">
            <div className="flex items-center gap-2 min-w-max">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-white font-medium'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5 text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Novel Grid */}
        <section className="w-full px-4 md:px-6 py-8 md:py-10">
          <div className="max-w-6xl mx-auto">
            {categoryNovels.length > 0 ? (
              <>
                <p className="text-sm text-foreground-500 mb-6">
                  共找到 <strong className="text-foreground-800">{categoryNovels.length}</strong> 本{displayName}
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                  {categoryNovels.map((novel) => (
                    <NovelCard key={novel.id} novel={novel} />
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                  <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无小说推荐，去看看其他分类吧！</p>
                <Link
                  to="/"
                  className="inline-block mt-4 px-5 py-2 rounded-full bg-primary-500 text-white text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  返回首页
                </Link>
              </div>
            )}
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}